import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';

const EcommerceCheckoutModal = ({
  show,
  onHide,
  cartItems,
  canSeePrices,
  currentUser,
  onRequireAuth,
  onOrderCompleted,
  whatsappSalesNumber,
  orderMessageTemplate,
  companyName,
  storeConfig,
  companyId
}) => {
  const [formData, setFormData] = useState(() => {
    const dir = [currentUser?.d_domic, currentUser?.d_local].filter(Boolean).join(', ');
    return {
      nombre: currentUser?.d_razon || currentUser?.nombre || currentUser?.name || '',
      telefono: currentUser?.d_whatsapp || currentUser?.n_celul || currentUser?.telefono || '',
      email: currentUser?.d_email || currentUser?.email || '',
      direccion: dir || currentUser?.direccion || '',
      observaciones: ''
    };
  });

  const [orderSent, setOrderSent] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [whatsappSent, setWhatsappSent] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [savedNptNumber, setSavedNptNumber] = useState(null);
  const [isSavingNpt, setIsSavingNpt] = useState(false);

  // Snapshot de ítems para no perder el resumen si el carrito se vacía
  const [itemsSnapshot, setItemsSnapshot] = useState(cartItems || []);
  useEffect(() => {
    if (show && cartItems && cartItems.length > 0) {
      setItemsSnapshot(cartItems);
    }
  }, [show, cartItems]);

  const activeItems = (itemsSnapshot && itemsSnapshot.length > 0) ? itemsSnapshot : (cartItems || []);

  // Sincronizar y limpiar datos de usuario / cliente autenticado
  useEffect(() => {
    if (currentUser) {
      const dir = [currentUser.d_domic, currentUser.d_local].filter(Boolean).join(', ');
      setFormData({
        nombre: currentUser.d_razon || currentUser.nombre || currentUser.name || '',
        telefono: currentUser.d_whatsapp || currentUser.n_celul || currentUser.telefono || '',
        email: currentUser.d_email || currentUser.email || '',
        direccion: dir || currentUser.direccion || '',
        observaciones: ''
      });
    } else {
      setFormData({
        nombre: '',
        telefono: '',
        email: '',
        direccion: '',
        observaciones: ''
      });
    }
  }, [currentUser, show]);

  // Reset de estados cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      setWhatsappSent(false);
      setEmailSent(false);
      setOrderSent(false);
      setSavedNptNumber(null);
      setIsSavingNpt(false);
    }
  }, [show]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const subtotal = activeItems.reduce((acc, item) => {
    const price = Number(item.unitPrice || item.price || item.i_unita || item.i_lista || 0);
    return acc + price * (item.quantity || 1);
  }, 0);

  const totalItemsCount = activeItems.reduce((acc, item) => acc + (item.quantity || 1), 0);

  // Crear la Nota de Pedido Tienda (NPT) en el backend si el switch está activado o si es forzado por envío directo
  const ensureNptCreated = async (force = false) => {
    // Si la configuración no lo requiere o ya fue creado para esta orden, reutilizar
    if (!storeConfig?.commercial?.saveOrderAsQuote && !force) {
      return null;
    }
    if (savedNptNumber) {
      return savedNptNumber;
    }

    setIsSavingNpt(true);
    try {
      const customerNotes = [
        'Pedido generado desde la Tienda Online (Web)',
        formData.telefono ? `Tel/WhatsApp: ${formData.telefono}` : null,
        formData.email ? `Email: ${formData.email}` : null,
        formData.direccion ? `Dirección de Entrega: ${formData.direccion}` : null,
        formData.observaciones ? `Observaciones: ${formData.observaciones}` : null
      ].filter(Boolean).join('\n');

      const headerData = {
        c_compr: 'NPT',
        t_presu: 'X',
        c_clien: currentUser?.c_clien || 1,
        c_flete: currentUser?.c_flete || 0,
        d_razon: (formData.nombre || currentUser?.d_razon || 'Consumidor Final').slice(0, 60),
        d_domic: (formData.direccion || currentUser?.d_domic || '').slice(0, 60),
        n_telef: (formData.telefono || currentUser?.n_telef || '').slice(0, 30),
        d_email: (formData.email || currentUser?.d_email || '').slice(0, 5),
        d_obser: customerNotes,
        i_grava: subtotal,
        i_total: subtotal
      };

      const itemsData = activeItems.map((item) => {
        const hasVariant = item.selectedVariant?.name && 
          !['Estándar', 'Estandar', 'std'].includes(item.selectedVariant.id) && 
          item.selectedVariant.name !== 'Estándar';
        const variantStr = hasVariant ? ` | Var: ${item.selectedVariant.name}` : '';
        const sizeStr = item.selectedSize?.nombre ? ` | Talle: ${item.selectedSize.nombre}` : '';
        
        let desc = item.d_artic || item.name || 'Artículo sin descripción';
        if (variantStr || sizeStr) {
          desc += ` (${variantStr}${sizeStr})`.replace('( |', '(');
        }

        const unitPrice = Number(item.unitPrice || item.price || item.i_unita || item.i_lista || item.i_publi || item.i_campre || item.i_precio || 0);
        const qty = item.quantity || 1;
        const lineTotal = unitPrice * qty;

        return {
          c_artic: item.c_artic || item.id,
          d_artic: desc.slice(0, 60),
          q_artic: qty,
          i_unita: unitPrice,
          i_lista: unitPrice,
          i_publi: unitPrice,
          i_campre: unitPrice,
          i_precio: unitPrice,
          i_total: lineTotal,
          i_venta: lineTotal,
          t_item: 'ART'
        };
      });

      const payload = {
        header: headerData,
        items: itemsData
      };

      const headers = companyId ? { 'X-Company-ID': String(companyId) } : {};
      const res = await axios.post('/api/presupuestos/', payload, {
        headers
      });

      const nptNum = res.data?.n_presu || res.data?.id;
      if (nptNum) {
        setSavedNptNumber(nptNum);
        return nptNum;
      }
    } catch (err) {
      console.error('Error al generar NPT en el sistema:', err);
    } finally {
      setIsSavingNpt(false);
    }
    return null;
  };

  // Enviar pedido directo a la tienda (sin abrir WhatsApp o Email)
  const handleSendDirectOrder = async () => {
    if (!currentUser) {
      Swal.fire({
        title: '¡Identificación requerida!',
        text: 'Para poder finalizar la compra y enviar tu pedido, debes iniciar sesión o registrarte con tu correo.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd',
        confirmButtonText: 'Registrarme / Ingresar'
      }).then(() => {
        if (onRequireAuth) onRequireAuth();
      });
      return;
    }

    if (!formData.nombre.trim()) {
      Swal.fire({
        title: 'Nombre requerido',
        text: 'Por favor completá tu nombre o razón social para enviar el pedido.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    if (!formData.telefono.trim() && !formData.email.trim()) {
      Swal.fire({
        title: 'Contacto requerido',
        text: 'Por favor ingresá un teléfono o correo electrónico para que podamos contactarte sobre tu pedido.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    setIsSavingNpt(true);
    const nptNum = await ensureNptCreated(true);
    const currentOrderRef = nptNum 
      ? `NPT-${String(nptNum).padStart(6, '0')}` 
      : (orderNumber || `PED-${Math.floor(100000 + Math.random() * 900000)}`);
    setOrderNumber(currentOrderRef);
    setIsSavingNpt(false);

    if (onOrderCompleted) {
      onOrderCompleted();
    }
    setOrderSent(true);
  };

  // Generar mensaje estructurado y prolijo para WhatsApp
  const handleSendWhatsApp = async () => {
    if (!currentUser) {
      Swal.fire({
        title: '¡Identificación requerida!',
        text: 'Para poder finalizar la compra y enviar tu pedido, debes iniciar sesión o registrarte con tu correo.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd',
        confirmButtonText: 'Registrarme / Ingresar'
      }).then(() => {
        if (onRequireAuth) onRequireAuth();
      });
      return;
    }

    if (!formData.nombre.trim() || !formData.telefono.trim()) {
      Swal.fire({
        title: 'Datos requeridos',
        text: 'Por favor completá tu nombre y teléfono para procesar el pedido por WhatsApp.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    const nptNum = await ensureNptCreated();
    const currentOrderRef = nptNum 
      ? `NPT-${String(nptNum).padStart(6, '0')}` 
      : (orderNumber || `PED-${Math.floor(100000 + Math.random() * 900000)}`);
    setOrderNumber(currentOrderRef);

    // Sanitizar texto para evitar colisiones con el formateo markdown de WhatsApp (*, _, ~)
    const sanitize = (text) => (text || '').replace(/[*_~`]/g, '').trim();
    const divider = '────────────────────────';

    let msg = `*PEDIDO ONLINE (${currentOrderRef}) - ${companyName || 'HIPERPEL S.A.'}*\n`;
    msg += `${divider}\n\n`;

    msg += `*DATOS DEL CLIENTE:*\n`;
    msg += `• *Nombre:* ${sanitize(formData.nombre)}\n`;
    msg += `• *Teléfono:* ${sanitize(formData.telefono)}\n`;
    if (formData.email && formData.email.trim()) {
      msg += `• *Email:* ${sanitize(formData.email)}\n`;
    }
    if (formData.direccion && formData.direccion.trim()) {
      msg += `• *Dirección:* ${sanitize(formData.direccion)}\n`;
    }
    if (formData.observaciones && formData.observaciones.trim()) {
      msg += `• *Observaciones:* ${sanitize(formData.observaciones)}\n`;
    }

    msg += `\n${divider}\n\n`;
    msg += `*DETALLE DEL PEDIDO (${totalItemsCount} ${totalItemsCount === 1 ? 'unidad' : 'unidades'} en total):*\n\n`;

    activeItems.forEach((item, index) => {
      const desc = sanitize(item.d_artic || item.name || 'Artículo sin descripción');
      const code = sanitize(item.c_artic || item.id || 'S/C');
      const hasVariant = item.selectedVariant?.name && 
        !['Estándar', 'Estandar', 'std'].includes(item.selectedVariant.id) && 
        item.selectedVariant.name !== 'Estándar';
      const variantStr = hasVariant ? ` | Var: ${sanitize(item.selectedVariant.name)}` : '';
      const sizeStr = item.selectedSize?.nombre ? ` | Talle: ${sanitize(item.selectedSize.nombre)}` : '';

      const unitPrice = Number(item.unitPrice || item.price || item.i_unita || item.i_lista || 0);
      const priceStr = canSeePrices
        ? (unitPrice > 0 
            ? `$${(unitPrice * (item.quantity || 1)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` 
            : 'A cotizar ($0,00)')
        : 'Precio tras login';

      msg += `*${index + 1}.* *${desc}*\n`;
      msg += `   • Cód: ${code}${variantStr}${sizeStr}\n`;
      msg += `   • Cantidad: *${item.quantity || 1} un.* | Subtotal: *${priceStr}*\n\n`;
    });

    msg += `${divider}\n`;
    if (canSeePrices && subtotal > 0) {
      msg += `*TOTAL ESTIMADO:* *$${Number(subtotal).toLocaleString('es-AR', { minimumFractionDigits: 2 })}* (s/IVA)\n`;
      msg += `${divider}\n\n`;
    } else {
      msg += `\n`;
    }

    msg += `_Mensaje generado automáticamente desde el catálogo online de ${companyName || 'la empresa'}._`;

    const cleanTargetPhone = (whatsappSalesNumber || '5491142476622').toString().replace(/\D/g, '');
    const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const whatsappUrl = isMobileOrTablet
      ? `https://api.whatsapp.com/send?phone=${cleanTargetPhone}&text=${encodeURIComponent(msg)}`
      : `https://web.whatsapp.com/send?phone=${cleanTargetPhone}&text=${encodeURIComponent(msg)}`;

    window.open(whatsappUrl, '_blank');
    setWhatsappSent(true);

    if (onOrderCompleted) {
      onOrderCompleted();
    }
  };

  const handleSendEmail = async () => {
    if (!currentUser) {
      Swal.fire({
        title: '¡Identificación requerida!',
        text: 'Para poder finalizar la compra y enviar tu pedido por email, debes iniciar sesión o registrarte con tu correo.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd',
        confirmButtonText: 'Registrarme / Ingresar'
      }).then(() => {
        if (onRequireAuth) onRequireAuth();
      });
      return;
    }

    if (!formData.nombre.trim()) {
      Swal.fire({
        title: 'Nombre requerido',
        text: 'Por favor completá tu nombre para enviar el pedido por email.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    if (!formData.email.trim()) {
      Swal.fire({
        title: 'Email requerido',
        text: 'Por favor completá tu dirección de correo electrónico para enviar el pedido.',
        icon: 'warning',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    const nptNum = await ensureNptCreated();
    const currentOrderRef = nptNum 
      ? `NPT-${String(nptNum).padStart(6, '0')}` 
      : (orderNumber || `PED-${Math.floor(100000 + Math.random() * 900000)}`);
    setOrderNumber(currentOrderRef);

    const divider = '========================================';
    let body = `PEDIDO ONLINE (${currentOrderRef}) - ${companyName || 'HIPERPEL S.A.'}\n`;
    body += `${divider}\n\n`;

    body += `DATOS DEL CLIENTE:\n`;
    body += `• Nombre / Razón Social: ${formData.nombre.trim()}\n`;
    if (formData.telefono && formData.telefono.trim()) {
      body += `• Teléfono: ${formData.telefono.trim()}\n`;
    }
    body += `• Email: ${formData.email.trim()}\n`;
    if (formData.direccion && formData.direccion.trim()) {
      body += `• Dirección de Entrega: ${formData.direccion.trim()}\n`;
    }
    if (formData.observaciones && formData.observaciones.trim()) {
      body += `• Observaciones: ${formData.observaciones.trim()}\n`;
    }

    body += `\n${divider}\n`;
    body += `DETALLE DEL PEDIDO (${totalItemsCount} ${totalItemsCount === 1 ? 'unidad' : 'unidades'} en total):\n\n`;

    activeItems.forEach((item, index) => {
      const desc = item.d_artic || item.name || 'Artículo sin descripción';
      const code = item.c_artic || item.id || 'S/C';
      const hasVariant = item.selectedVariant?.name && 
        !['Estándar', 'Estandar', 'std'].includes(item.selectedVariant.id) && 
        item.selectedVariant.name !== 'Estándar';
      const variantStr = hasVariant ? ` | Var: ${item.selectedVariant.name}` : '';
      const sizeStr = item.selectedSize?.nombre ? ` | Talle: ${item.selectedSize.nombre}` : '';

      const unitPrice = Number(item.unitPrice || item.price || item.i_unita || item.i_lista || 0);
      const priceStr = canSeePrices
        ? (unitPrice > 0 
            ? `$${(unitPrice * (item.quantity || 1)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` 
            : 'A cotizar ($0,00)')
        : 'Precio tras login';

      body += `${index + 1}. [${code}] ${desc}${variantStr}${sizeStr}\n`;
      body += `   Cantidad: ${item.quantity || 1} un. | Subtotal: ${priceStr}\n\n`;
    });

    body += `${divider}\n`;
    if (canSeePrices && subtotal > 0) {
      body += `TOTAL ESTIMADO: $${Number(subtotal).toLocaleString('es-AR', { minimumFractionDigits: 2 })} (s/IVA)\n`;
      body += `${divider}\n\n`;
    } else {
      body += `\n`;
    }

    body += `Mensaje generado automáticamente desde el catálogo online de ${companyName || 'la empresa'}.`;

    const subject = `Nuevo Pedido Online [${currentOrderRef}] - ${formData.nombre.trim()}`;
    const destinationEmail = storeConfig?.footer?.email || 'ventas@hiperpel.com.ar';
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${destinationEmail}&su=${encodedSubject}&body=${encodedBody}`;
    const outlookUrl = `https://outlook.live.com/mail/0/deeplink/compose?to=${destinationEmail}&subject=${encodedSubject}&body=${encodedBody}`;
    const mailtoUrl = `mailto:${destinationEmail}?subject=${encodedSubject}&body=${encodedBody}`;

    const completeOrder = () => {
      setEmailSent(true);
      if (onOrderCompleted) {
        onOrderCompleted();
      }
    };

    Swal.fire({
      title: 'Enviar Pedido por Email',
      html: `
        <div class="text-start">
          <p class="text-muted small mb-3">
            Elegí cómo querés redactar el pedido dirigido a <b>${destinationEmail}</b>:
          </p>
          <div class="d-grid gap-2">
            <button id="swal-btn-gmail" type="button" class="btn btn-outline-danger fw-semibold d-flex align-items-center justify-content-between p-2 rounded-3 shadow-none">
              <span class="d-flex align-items-center"><i class="bi bi-google me-2 fs-5"></i> Abrir en Gmail Web</span>
              <span class="badge bg-danger-subtle text-danger border border-danger-subtle">Recomendado</span>
            </button>
            <button id="swal-btn-outlook" type="button" class="btn btn-outline-primary fw-semibold d-flex align-items-center p-2 rounded-3 shadow-none">
              <i className="bi bi-microsoft me-2 fs-5"></i> Abrir en Outlook Web / Hotmail
            </button>
            <button id="swal-btn-mailto" type="button" class="btn btn-outline-secondary fw-semibold d-flex align-items-center p-2 rounded-3 shadow-none">
              <i className="bi bi-envelope-fill me-2 fs-5"></i> App de Correo (Outlook / Windows)
            </button>
            <button id="swal-btn-copy" type="button" class="btn btn-light border fw-semibold d-flex align-items-center p-2 rounded-3 text-dark shadow-none">
              <i className="bi bi-clipboard-check me-2 fs-5 text-success"></i> Copiar Resumen al Portapapeles
            </button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        const gmailBtn = document.getElementById('swal-btn-gmail');
        const outlookBtn = document.getElementById('swal-btn-outlook');
        const mailtoBtn = document.getElementById('swal-btn-mailto');
        const copyBtn = document.getElementById('swal-btn-copy');

        if (gmailBtn) {
          gmailBtn.onclick = () => {
            window.open(gmailUrl, '_blank');
            Swal.close();
            completeOrder();
          };
        }

        if (outlookBtn) {
          outlookBtn.onclick = () => {
            window.open(outlookUrl, '_blank');
            Swal.close();
            completeOrder();
          };
        }

        if (mailtoBtn) {
          mailtoBtn.onclick = () => {
            window.location.href = mailtoUrl;
            Swal.close();
            completeOrder();
          };
        }

        if (copyBtn) {
          copyBtn.onclick = () => {
            if (navigator.clipboard) {
              navigator.clipboard.writeText(`Para: ${destinationEmail}\nAsunto: ${subject}\n\n${body}`);
            }
            Swal.fire({
              title: '¡Resumen Copiado!',
              text: 'El detalle del pedido se copió al portapapeles para que lo pegues en tu correo.',
              icon: 'success',
              timer: 1800,
              showConfirmButton: false
            }).then(() => {
              completeOrder();
            });
          };
        }
      }
    });
  };

  const handleClose = () => {
    setOrderSent(false);
    setWhatsappSent(false);
    setEmailSent(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="border-bottom px-4 py-3">
        <Modal.Title className="fw-bold fs-5 text-dark d-flex align-items-center gap-2">
          <i className="bi bi-bag-check-fill text-primary"></i> Finalizar Pedido
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {orderSent ? (
          <div className="text-center py-4">
            <div
              className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm mb-3"
              style={{ width: '76px', height: '76px' }}
            >
              <i className="bi bi-check-lg" style={{ fontSize: '2.5rem' }}></i>
            </div>
            <h4 className="fw-bold text-dark mb-2">¡Pedido generado con éxito!</h4>
            <p className="text-muted mb-3">
              Número de referencia de pedido: <span className="badge bg-primary-subtle text-primary border border-primary-subtle fs-6 font-monospace px-3 py-1 ms-1">{orderNumber}</span>
            </p>
            <Alert variant="info" className="small d-inline-block text-start" style={{ maxWidth: '480px' }}>
              <i className="bi bi-info-circle-fill me-1"></i> Tu pedido ha sido preparado y registrado. Un asesor comercial se pondrá en contacto a la brevedad para coordinar la entrega y facturación.
            </Alert>
            <div className="mt-4">
              <Button variant="primary" className="rounded-pill px-4 fw-semibold shadow-sm" onClick={handleClose}>
                Volver a la Tienda
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {whatsappSent && (
              <Alert variant="success" className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between p-3 mb-3 border-success-subtle shadow-xs gap-3">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-whatsapp fs-4 text-success flex-shrink-0"></i>
                  <div className="small">
                    <strong>¡WhatsApp abierto con éxito!</strong> Pedido: <span className="badge bg-success-subtle text-success border border-success-subtle font-monospace px-2 py-1">{orderNumber}</span>
                    <span className="d-block text-muted">Podés reabrir la ventana de chat, enviar también una copia por email o finalizar la compra cuando gustes.</span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <Button variant="outline-success" size="sm" className="fw-bold text-nowrap rounded-pill px-3" onClick={handleSendWhatsApp}>
                    <i className="bi bi-arrow-repeat me-1"></i> Reabrir WhatsApp
                  </Button>
                  <Button variant="success" size="sm" className="fw-bold text-nowrap rounded-pill px-3" onClick={() => setOrderSent(true)}>
                    <i className="bi bi-check2-circle me-1"></i> Finalizar
                  </Button>
                </div>
              </Alert>
            )}

            {emailSent && (
              <Alert variant="primary" className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between p-3 mb-3 border-primary-subtle shadow-xs gap-3">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-envelope-check-fill fs-4 text-primary flex-shrink-0"></i>
                  <div className="small">
                    <strong>¡Pedido enviado por Email!</strong> Referencia: <span className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace px-2 py-1">{orderNumber}</span>
                    <span className="d-block text-muted">Podés redactar nuevamente el correo, enviarlo también por WhatsApp o finalizar la compra cuando gustes.</span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <Button variant="outline-primary" size="sm" className="fw-bold text-nowrap rounded-pill px-3" onClick={handleSendEmail}>
                    <i className="bi bi-arrow-repeat me-1"></i> Reabrir Email
                  </Button>
                  <Button variant="primary" size="sm" className="fw-bold text-nowrap rounded-pill px-3" onClick={() => setOrderSent(true)}>
                    <i className="bi bi-check2-circle me-1"></i> Finalizar
                  </Button>
                </div>
              </Alert>
            )}

            <Row className="g-4">
              {/* Columna Izquierda: Datos del Comprador */}
              <Col lg={7} md={12}>
                {!currentUser && (
                  <Alert variant="warning" className="d-flex align-items-center justify-content-between mb-4 border-warning shadow-xs p-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-exclamation-triangle-fill fs-5 text-warning"></i>
                      <span className="small">
                        <strong>Identificación requerida:</strong> Debes ingresar o registrarte para confirmar el pedido.
                      </span>
                    </div>
                    <Button variant="warning" size="sm" className="fw-bold px-3 text-dark text-nowrap" onClick={onRequireAuth}>
                      Ingresar
                    </Button>
                  </Alert>
                )}

                <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-person-lines-fill text-primary"></i> Datos de Contacto y Entrega
                </h6>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold text-muted">Nombre y Apellido / Razón Social *</Form.Label>
                    <Form.Control
                      type="text"
                      name="nombre"
                      placeholder="Ej. Juan Pérez / Librería El Faro"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Row className="g-2 mb-3">
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">Teléfono / WhatsApp *</Form.Label>
                        <Form.Control
                          type="text"
                          name="telefono"
                          placeholder="Ej. 11 9876-5432"
                          value={formData.telefono}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">Correo Electrónico</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder="ejemplo@correo.com"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold text-muted">Dirección de Entrega / Localidad</Form.Label>
                    <Form.Control
                      type="text"
                      name="direccion"
                      placeholder="Calle, Número, Localidad y Provincia"
                      value={formData.direccion}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label className="small fw-semibold text-muted">Notas u Observaciones del Pedido</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="observaciones"
                      placeholder="Horarios de entrega, aclaraciones sobre variantes, etc."
                      value={formData.observaciones}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Form>
              </Col>

              {/* Columna Derecha: Resumen del Pedido y Botones de Acción */}
              <Col lg={5} md={12}>
                <div className="bg-light p-3 rounded-4 border d-flex flex-column h-100 justify-content-between">
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="fw-bold text-dark mb-0">Resumen</h6>
                      <span className="badge bg-secondary-subtle text-dark rounded-pill">
                        {totalItemsCount} {totalItemsCount === 1 ? 'ítem' : 'ítems'}
                      </span>
                    </div>

                    {/* Listado scrolleable de productos */}
                    <div className="overflow-auto pe-1 mb-3" style={{ maxHeight: '190px' }}>
                      {activeItems.map((item, idx) => {
                        const itemImg = item.primaryUrl || item.d_imagen?.split(/[,;|]/)[0]?.trim() || item.imagen || item.foto;
                        const resolvedImg = itemImg ? (itemImg.startsWith('http') || itemImg.startsWith('/') ? itemImg : `/media/articulos/${itemImg}`) : null;
                        const qty = item.quantity || 1;
                        const unitPrice = Number(item.unitPrice || item.price || item.i_unita || item.i_lista || 0);
                        return (
                          <div key={idx} className="d-flex align-items-center justify-content-between py-2 border-bottom small gap-2">
                            <div className="d-flex align-items-center gap-2 text-truncate min-w-0 flex-grow-1" style={{ minWidth: 0 }}>
                              {resolvedImg ? (
                                <img 
                                  src={resolvedImg} 
                                  alt={item.d_artic} 
                                  className="rounded border flex-shrink-0" 
                                  style={{ width: '32px', height: '32px', objectFit: 'contain', background: '#ffffff' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="rounded border flex-shrink-0 d-flex align-items-center justify-content-center bg-white text-muted" style={{ width: '32px', height: '32px' }}>
                                  <i className="bi bi-box-seam" style={{ fontSize: '0.85rem' }}></i>
                                </div>
                              )}
                              <div className="text-truncate min-w-0 flex-grow-1" style={{ minWidth: 0 }}>
                                <span className="badge bg-primary-subtle text-primary me-1">{qty}x</span>
                                <span className="fw-semibold text-dark">{item.d_artic}</span>
                                {item.selectedVariant?.name && item.selectedVariant?.name !== 'Estándar' && (
                                  <span className="text-muted d-block small" style={{ fontSize: '0.72rem' }}>
                                    Var: {item.selectedVariant.name}
                                  </span>
                                )}
                                {item.selectedSize?.nombre && (
                                  <span className="text-primary d-block small fw-semibold" style={{ fontSize: '0.72rem' }}>
                                    Talle: {item.selectedSize.nombre}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="text-end flex-shrink-0">
                              {canSeePrices ? (
                                unitPrice > 0 ? (
                                  <span className="fw-bold text-dark">
                                    ${(unitPrice * qty).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                  </span>
                                ) : (
                                  <span className="badge bg-light text-muted border small">
                                    A cotizar
                                  </span>
                                )
                              ) : (
                                <span className="text-muted font-monospace small">
                                  Precio tras login
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Total si hay precios visibles */}
                    {canSeePrices && subtotal > 0 && (
                      <div className="border-top pt-2 mb-3">
                        <div className="d-flex justify-content-between fw-bold text-dark fs-6">
                          <span>Total estimado:</span>
                          <span className="text-primary">${subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botones de acción integrados dentro de la tarjeta */}
                  <div className="d-flex flex-column gap-2 mt-3 pt-2 border-top">
                    {/* Botón Principal: Confirmar y Enviar Pedido a la Tienda */}
                    <Button
                      variant="primary"
                      className="fw-bold py-2 px-3 w-100 d-flex align-items-center justify-content-center gap-2 shadow-sm rounded-3"
                      onClick={handleSendDirectOrder}
                      disabled={isSavingNpt}
                    >
                      {isSavingNpt ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>Registrando pedido...</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-bag-check-fill fs-5"></i>
                          <span>Confirmar Pedido</span>
                        </>
                      )}
                    </Button>

                    {/* Separador con Flexbox limpio y alineado */}
                    <div className="d-flex align-items-center my-1">
                      <div className="flex-grow-1 border-top border-secondary-subtle"></div>
                      <span className="px-2 text-muted fw-semibold text-nowrap" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>
                        O ENVIAR POR
                      </span>
                      <div className="flex-grow-1 border-top border-secondary-subtle"></div>
                    </div>

                    {/* Opciones 2 y 3: WhatsApp y Email con diseño limpio y armónico */}
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-success"
                        className="fw-semibold py-2 w-50 d-flex align-items-center justify-content-center gap-1 rounded-3 bg-white text-success border-success-subtle shadow-xs small"
                        onClick={handleSendWhatsApp}
                        disabled={isSavingNpt}
                        title="Enviar el pedido detallado por WhatsApp a la empresa"
                      >
                        <i className="bi bi-whatsapp fs-6"></i>
                        <span>{whatsappSent ? 'Reenviar' : 'WhatsApp'}</span>
                      </Button>

                      <Button
                        variant="outline-primary"
                        className="fw-semibold py-2 w-50 d-flex align-items-center justify-content-center gap-1 rounded-3 bg-white text-primary border-primary-subtle shadow-xs small"
                        onClick={handleSendEmail}
                        disabled={isSavingNpt}
                        title="Enviar el pedido detallado por Email"
                      >
                        <i className="bi bi-envelope-fill fs-6"></i>
                        <span>{emailSent ? 'Reenviar' : 'Email'}</span>
                      </Button>
                    </div>

                    <div className="text-center mt-1">
                      <span className="text-muted d-block" style={{ fontSize: '0.72rem', lineHeight: '1.25' }}>
                        <i className="bi bi-shield-check text-success me-1"></i>
                        Un asesor se contactará para coordinar la entrega.
                      </span>
                    </div>
                  </div>

                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EcommerceCheckoutModal;
